import {
  ComponentProps,
  ElementType,
  FormEvent,
  forwardRef,
  useMemo,
} from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import {
  Dialog,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import { Context, DialogContext, useDialogContext } from './context';

// This is a synthetic form event that can have preventDefault called on it.
// This is used to prevent the dialog from closing when a form is submitted.
class SyntheticFormEvent extends Event {
  private _defaultPrevented: boolean = false;
  public submitter?: HTMLElement;

  constructor(
    type: string,
    eventInitDict?: EventInit & { submitter?: HTMLElement },
  ) {
    super(type, eventInitDict);
    this.submitter = eventInitDict?.submitter;
  }

  preventDefault(): void {
    this._defaultPrevented = true;
  }

  isPreventDefault(): boolean {
    return this._defaultPrevented;
  }
}

const styles = createStyles({
  root: {},
  component: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    overflow: 'hidden',
  },
});

export type ContentProps = WithStyles<typeof styles> & {
  isLoading?: boolean;
  component?: ElementType;
  className?: string;
} & Omit<
    ComponentProps<ElementType>,
    'className' | 'children' | 'classes' | 'onSubmit'
  > & {
    onSubmit?: (event: FormEvent<HTMLElement>) => void | Promise<void>;
  };

export const Content = forwardRef<HTMLDivElement, ContentProps>(
  (props, ref) => {
    const {
      id,
      open,
      onClose,
      props: dialogProps,
      static: staticProp,
      onOpen,
      isLoading,
    } = useDialogContext();

    const {
      component: Component = 'div',
      classes,
      className,
      onSubmit,
      ...rest
    } = props;

    const [{ loading }, handleSubmit] = useAsyncFn(
      async (event: FormEvent<HTMLElement>) => {
        event.preventDefault();

        // Capture the submitter before creating the synthetic event
        const nativeEvent = event.nativeEvent as SubmitEvent;
        const submitter = nativeEvent?.submitter ?? undefined;

        // Must use a synthetic event here, because we must prevent the actual
        // form event directly, otherwise the page will reload as soon as we
        // call onSubmit and it starts doing it's business.
        const syntheticEvent = new SyntheticFormEvent('submit', {
          bubbles: true,
          cancelable: true,
          submitter,
        });

        await onSubmit?.(syntheticEvent);

        const isPrevented = syntheticEvent.isPreventDefault();
        if (!isPrevented) {
          onClose();
        }
      },
      [onSubmit, onClose],
    );

    const value: Context = useMemo(
      () => ({
        id,
        isLoading: loading || isLoading,
        open,
        onClose,
        props: dialogProps,
        static: staticProp,
        onOpen,
      }),
      [id, loading, isLoading, open, onClose, dialogProps, staticProp, onOpen],
    );

    // if "static" is true, the content is not rendered inside a dialog modal.
    if (staticProp) {
      return (
        <DialogContext.Provider value={value}>
          <Component
            {...rest}
            onSubmit={Component === 'form' ? handleSubmit : undefined}
            ref={ref}
            className={classNames(classes.component, className)}
          />
        </DialogContext.Provider>
      );
    }

    return (
      <Dialog
        open={open}
        onClose={onClose}
        className={classes.root}
        aria-labelledby={`${id}-title`}
        {...dialogProps}
      >
        <DialogContext.Provider value={value}>
          <Component
            {...rest}
            onSubmit={Component === 'form' ? handleSubmit : undefined}
            ref={ref}
            className={classNames(classes.component, className)}
          />
        </DialogContext.Provider>
      </Dialog>
    );
  },
);

Content.displayName = 'DialogComposite.Content';

export default withStyles(styles)(Content);
